import {
  BugReportOutlined,
  DeviceUnknownOutlined,
  MiscellaneousServicesOutlined,
  PauseCircleOutlineOutlined,
} from "@mui/icons-material";
import RocketOutlinedIcon from "@mui/icons-material/RocketOutlined";
import { Button, Tooltip } from "@mui/material";
import {
  MRT_ColumnDef,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useSwitchGsModeMutation, useSwitchTesModeMutation } from "@/api/hooks/command-hooks";
import { usePlatformServicesFromAllRoomsQuery } from "@/api/hooks/platform-service-hooks";
import ASelectPopover from "@/components/a-select-popover";
import { getEnumNumberValues, mapExploitationStageToStringName } from "@/lib/common-utils";
import { COMMON_REFETCH_INTERVAL, ExploitationStage } from "@/lib/constants";
import { useRunningServicesTableColumns } from "@/module/running-services-module/running-services-table-columns";
import { Route as ErrorPageRoute } from "@/routes/error/index";
import {
  PlatformServiceRegularDto,
  PlatformServiceRegularDtoTypeEnum,
  SwitchGsModeModeEnum,
  SwitchTesModeModeEnum,
} from "@/shared/api";

import { Route } from "../../routes/instance";

const RunningServicesModule = () => {
  const { t } = useTranslation();
  const [refetchInterval, setRefetchInterval] = useState<number>(COMMON_REFETCH_INTERVAL);
  const [innerModes, setInnerModes] = useState<Record<string, number>>({});

  const {
    data: runningInstancesRs,
    isLoading: runningInstancesIsLoading,
    isError: runningInstancesIsError,
    error: runningInstancesError,
  } = usePlatformServicesFromAllRoomsQuery({
    refetchInterval: refetchInterval,
  });

  function startFastRefetch() {
    setRefetchInterval(1000);
    setTimeout(() => {
      setRefetchInterval(COMMON_REFETCH_INTERVAL);
    }, COMMON_REFETCH_INTERVAL); //in 15 sec
  }

  const [runningInstances, setRunningInstances] = useState(() => {
    return runningInstancesRs?.data ?? [];
  });

  useEffect(() => {
    const idsToDelete: string[] = [];
    const data = runningInstancesRs?.data || [];
    data.forEach((row) => {
      if (innerModes[row.id] !== undefined && innerModes[row.id] === row.mode) {
        idsToDelete.push(row.id);
      }
    });
    if (idsToDelete.length) {
      setInnerModes((prevState) => {
        const newState = { ...prevState };

        idsToDelete.forEach((id) => {
          delete newState[id];
        });

        return newState;
      });
    }
    setRunningInstances(
      data.map((item) => {
        return {
          ...item,
          mode: innerModes[item.id] === undefined ? item.mode : innerModes[item.id],
        };
      }) ?? []
    );
  }, [runningInstancesRs?.data, innerModes]);

  const switchTesModeMutation = useSwitchTesModeMutation();
  const switchGsModeMutation = useSwitchGsModeMutation();

  const [changingExploitationStageService, setChangingExploitationStageService] = useState<
    string | undefined
  >(undefined);

  const [newExploitationStageValue, setNewExploitationStageValue] = useState<
    ExploitationStage | undefined
  >(undefined);

  const navigate = Route.useNavigate();

  useEffect(() => {
    if (runningInstancesIsError) {
      void navigate({
        to: ErrorPageRoute.fullPath,
        search: { errorMessage: runningInstancesError.message },
      });
    }
  }, [navigate, runningInstancesError, runningInstancesIsError]);

  const mapManageableServiceTitle = useCallback(
    (service: PlatformServiceRegularDto) => {
      const actualMode = innerModes[service.id] ? innerModes[service.id] : service.mode;
      const icon = (() => {
        switch (actualMode) {
          case ExploitationStage.PRODUCTION:
            return (
              <Tooltip sx={{ position: "relative", top: "4px" }} title={t("production")}>
                <RocketOutlinedIcon style={{ color: "green" }} />
              </Tooltip>
            );
          case ExploitationStage.TEST:
            return (
              <Tooltip sx={{ position: "relative", top: "4px" }} title={t("testing")}>
                <BugReportOutlined style={{ color: "gold" }} />
              </Tooltip>
            );
          case ExploitationStage.UNAVAILABLE:
            return (
              <Tooltip sx={{ position: "relative", top: "4px" }} title={t("unavailable")}>
                <PauseCircleOutlineOutlined style={{ color: "blue" }} />
              </Tooltip>
            );
          case ExploitationStage.SERVICE:
            return (
              <Tooltip sx={{ position: "relative", top: "4px" }} title={t("service")}>
                <MiscellaneousServicesOutlined style={{ color: "orange" }} />
              </Tooltip>
            );
          default:
            return (
              <Tooltip sx={{ position: "relative", top: "4px" }} title={t("unknownMode")}>
                <DeviceUnknownOutlined />
              </Tooltip>
            );
        }
      })();

      return (
        <>
          {icon} {/*{service.type} {service.room}*/}
        </>
      );
    },
    [t, innerModes]
  );

  const getServiceExploitationStageSelectOptions = useCallback(
    (currentStage: number | undefined) => {
      return getEnumNumberValues(ExploitationStage)
        .filter((item) => item !== currentStage)
        .map((item) => ({ label: mapExploitationStageToStringName(item), value: item }));
    },
    []
  );

  const resetSwitchExploitationStageState = useCallback(() => {
    setChangingExploitationStageService(undefined);
    setNewExploitationStageValue(undefined);
  }, []);

  // TODO temporal solution while scaling isn't implemented
  const handleSwitchMode = useCallback(
    (service: PlatformServiceRegularDto, mode: SwitchTesModeModeEnum) => {
      startFastRefetch();

      if (service.type === PlatformServiceRegularDtoTypeEnum.Tes) {
        handleSwitchTesExploitationStage(service, mode);
      } else {
        handleSwitchGsExploitationStage(service, mode);
      }

      function handleSwitchTesExploitationStage(
        service: PlatformServiceRegularDto,
        mode: SwitchTesModeModeEnum
      ) {
        const id = service.id;

        switchTesModeMutation.mutate(
          {
            domain: service.room,
            mode: mode,
            tesId: id,
          },
          {
            onSuccess: () => {
              setInnerModes((prevState) => {
                return {
                  ...prevState,
                  [service.id]: Number(mode),
                };
              });
              resetSwitchExploitationStageState();
              enqueueSnackbar<"success">(t("switchModeHasBeenSuccessfullyCompleted"));
            },
            onError: (err) => {
              enqueueSnackbar<"error">(t("somethingWentWrongWhileSwitchingMode"));
              console.error(`Something went wrong while while switching mode`, err);
            },
          }
        );
      }

      function handleSwitchGsExploitationStage(
        service: PlatformServiceRegularDto,
        mode: SwitchGsModeModeEnum
      ) {
        const id = service.id;

        switchGsModeMutation.mutate(
          {
            domain: service.room,
            gsId: id,
            mode: mode,
          },
          {
            onSuccess: () => {
              setInnerModes((prevState) => {
                return {
                  ...prevState,
                  [service.id]: Number(mode),
                };
              });
              resetSwitchExploitationStageState();
              enqueueSnackbar<"success">(t("switchModeHasBeenSuccessfullyCompleted"));
            },
            onError: (err) => {
              enqueueSnackbar<"error">(t("somethingWentWrongWhileSwitchingMode"));
              console.error(`Something went wrong while while switching mode`, err);
            },
          }
        );
      }
    },
    [resetSwitchExploitationStageState, switchGsModeMutation, switchTesModeMutation, t]
  );

  const simpleColumns = useRunningServicesTableColumns(innerModes);

  const columns = useMemo<MRT_ColumnDef<PlatformServiceRegularDto>[]>(
    () => [
      {
        header: t("status"),
        Cell: ({ row }) => {
          return mapManageableServiceTitle(row.original);
        },
      },
      {
        header: t("switchMode"),
        Cell: ({ row }) => (
          <ASelectPopover
            loading={
              row.original.type === "TES"
                ? switchTesModeMutation.isPending
                : switchGsModeMutation.isPending
            }
            containerStyle={{ display: "flex" }}
            buttonText={t("ok")}
            disabled={newExploitationStageValue === undefined}
            onOk={() =>
              handleSwitchMode(
                row.original,
                newExploitationStageValue as unknown as SwitchTesModeModeEnum
              )
            }
            popoverProps={{
              title: t("chooseMode"),
              open: row.original.id === changingExploitationStageService,
              trigger: "click",
              onOpenChange: (open) => {
                if (!open) resetSwitchExploitationStageState();
              },
            }}
            selectProps={{
              style: { width: 150 },
              value: newExploitationStageValue,
              onChange: (value) => setNewExploitationStageValue(value),
              allowClear: true,
              options: getServiceExploitationStageSelectOptions(row.original.mode),
              placeholder: t("chooseMode"),
            }}
          >
            <Button
              variant="contained"
              sx={{ fontSize: "8px" }}
              //size="small"
              onClick={() => setChangingExploitationStageService(row.original.id)}
            >
              {t("switchMode")}
            </Button>
          </ASelectPopover>
        ),
        enableColumnOrdering: false,
        enableSorting: false,
        size: 150,
      },
      ...simpleColumns,
    ],
    [
      t,
      simpleColumns,
      mapManageableServiceTitle,
      switchTesModeMutation,
      switchGsModeMutation,
      handleSwitchMode,
      resetSwitchExploitationStageState,
      changingExploitationStageService,
      newExploitationStageValue,
      setNewExploitationStageValue,
      setChangingExploitationStageService,
      getServiceExploitationStageSelectOptions,
    ]
  );

  const table = useMaterialReactTable({
    data: runningInstances ?? [],
    columns: columns,
    getRowId: (originalRow) => originalRow.id,
    //selectAllMode: "all",
    state: {
      isLoading: runningInstancesIsLoading,
      //...state,
      //sorting: state.sorting.length > 0 ? state.sorting : [{ id: "name", desc: false }],
    },
    //...defaultProps,
    initialState: {
      pagination: { pageSize: 100, pageIndex: 0 },
      density: "compact",
      sorting: [{ id: "name", desc: false }],
    },
    //enableRowActions: true,
    //enableSelectAll: true,
    //enableRowSelection: true,
    enableGrouping: true,
    enableColumnDragging: false,
    enableColumnOrdering: false,
    enableMultiSort: true,
    enableColumnFilterModes: true,
    enablePagination: true,
    //enableSorting: enableColumnSorting,
    muiTableHeadCellProps: {
      sx: {
        verticalAlign: "middle",
        "& *": {
          whiteSpace: "normal",
          lineHeight: 1.35,
        },
      },
    },
    defaultColumn: {
      enableResizing: false,
      grow: false,
      maxSize: 30,
      minSize: 10,
    },
    /*muiTableBodyRowProps: ({ row }) => { //Выделение жёлтым строк где дилей больше 11 секунд
      const updatedAt = row.original.updated_at
        ? new Date(row.original.updated_at).getTime()
        : null;
      const now = Date.now();
      const diffSeconds = updatedAt ? (now - updatedAt) / 1000 : null;

      return {
        sx: {
          backgroundColor:
            diffSeconds !== null && diffSeconds > 11
              ? "rgba(255, 255, 0, 0.2)"
              : "inherit",
        },
      };
    },*/
    /*renderTopToolbarCustomActions: ({ table }) => {
      const selectedCount = table.getSelectedRowModel().rows.length;

      return (
        <div style={{ height: "40px", display: "flex", alignItems: "center" }}>
          {cfgPerm.write && (
            <Button onClick={() => {
              setModalInitValues(undefined);
              setModalMode("add");
            }}>
              <AddOutlined /> {t("new")}
            </Button>
          )}

          {selectedCount > 0 && (
            <div style={{ marginLeft: 30, display: "flex" }}>
              {cfgPerm.execute && (
                <IconButton
                  size="small"
                  style={{ fontSize: "15px" }}
                  color="success"
                  onClick={() => handleChangeEnableBulk(table, true)}
                >
                  <Power /> {t("enable")}
                </IconButton>
              )}
              {cfgPerm.execute && (
                <IconButton
                  size="small"
                  style={{ fontSize: "15px" }}
                  color="error"
                  onClick={() => handleChangeEnableBulk(table, false)}
                >
                  <PowerOff /> {t("disable")}
                </IconButton>
              )}

              {cfgPerm.delete && (
                <Popconfirm
                  title={t("deleteItems")}
                  description={t("areYouSureToDeleteItems")}
                  onConfirm={() => handleDeleteConfigs(table)}
                >
                  <IconButton size="small" style={{ fontSize: "15px" }} color="error">
                    <DeleteOutlineOutlined /> {t("delete")}
                  </IconButton>
                </Popconfirm>
              )}

              {isOwner && (
                <ApplyAuthGroupsTableControl
                  domain={room}
                  cfgName={ROOM_CONFIGS_NAMES.CMS_AGGREGATOR}
                  table={table}
                  filters={assembleNamesFilter(table)}
                  totalElementsCount={selectedCount}
                  onSuccess={() => void refetch()}
                />
              )}
            </div>
          )}
        </div>
      );
    },
    renderRowActions: ({ row }) => (
      <Box>
        <IconButton
          onClick={() => {
            const managerClubData = configsRs?.data.content.find((item) => item.id === row.original.id) as unknown as ClubManager;

            setModalInitValues({
              ...managerClubData,
              auth_groups: row.original.auth_groups ?? new Set(),
            })
            setEditItemName(managerClubData.name)
            setModalMode("edit");
          }}
        >
          <Edit />
        </IconButton>

        {cfgPerm.write && (
          <IconButton
            onClick={() => {
              const managerClubData = configsRs?.data.content.find((item) => item.id === row.original.id) as unknown as ClubManager;

              setModalInitValues({
                ...managerClubData,
                name: "",
                auth_groups: new Set()
              });
              setModalMode("add")
            }}
          >
            <ContentCopy />
          </IconButton>
        )}
      </Box>
    ),
    renderBottomToolbarCustomActions: () => (
      <>
        <FormControlLabel
          value={enableColumnDragging}
          control={
            <Switch
              size="small"
              onClick={() => setEnableColumnDragging(!enableColumnDragging)}
            />
          }
          label={t("columnsDragging")}
          labelPlacement="start"
        />
        <FormControlLabel
          value={enableColumnSorting}
          control={
            <Switch
              size="small"
              onClick={() => setEnableColumnSorting(!enableColumnSorting)}
            />
          }
          label={t("columnsSorting")}
          labelPlacement="start"
        />
      </>
    ),*/
  });

  return (
    <>
      <MaterialReactTable table={table} />
      {/*<Stack spacing={2}>
        {runningInstances.map((item) => (
          <Paper key={item.id} elevation={3} sx={{ p: 2 }}>
            <Box sx={{ position: "relative" }}>

              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography variant="h6">{mapManageableServiceTitle(item)}</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={3}>
                <Grid item xs={4}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <Box component="span" sx={{ color: "gray" }}>{t("id")}: </Box> {item.id ?? "-"}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <Box component="span" sx={{ color: "gray" }}>{t("version")}: </Box> {item.version ?? "-"}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <Box component="span" sx={{ color: "gray" }}>{t("mode")}: </Box> {item.mode ?? "-"}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <Box component="span" sx={{ color: "gray" }}>{t("room")}: </Box> {item.room}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <Box component="span" sx={{ color: "gray" }}>{t("serverPort")}: </Box> {item.server_port ?? "-"}
                  </Typography>
                </Grid>

                <Grid item xs={4}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <Box component="span" sx={{ color: "gray" }}>{t("type")}: </Box> {item.type}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <Box component="span" sx={{ color: "gray" }}>{t("description")}: </Box> {item.description ?? "-"}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <Box component="span" sx={{ color: "gray" }}>{t("serverAddress")}: </Box> {item.server_addr ?? "-"}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <Box component="span" sx={{ color: "gray" }}>{t("uptime")}: </Box> {item.uptime ? <TimeAgo pastDate={item.uptime} /> : "-"}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <Box component="span" sx={{ color: "gray" }}>{t("numberClients")}: </Box> {item.nclients || 0}
                  </Typography>
                </Grid>

                <Grid item xs={4}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <Box component="span" sx={{ color: "gray" }}>{t("numberThreads")}: </Box> {item.nthreads || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <Box component="span" sx={{ color: "gray" }}>{t("processId")}: </Box> {item.pid ?? "-"}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <Box component="span" sx={{ color: "gray" }}>{t("processRAMConsuming")}: </Box>
                    {item.process_ram_total != null && item.process_ram_free != null
                      ? t("serverRamValue", {
                        free: (item.process_ram_total < 0 ? 0 : item.process_ram_total) - (item.process_ram_free < 0 ? 0 : item.process_ram_free),
                        total: item.process_ram_total < 0 ? 0 : item.process_ram_total,
                      })
                      : t("unknown")}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <Box component="span" sx={{ color: "gray" }}>{t("serverRAMConsuming")}: </Box>
                    {item.server_ram_total != null && item.server_ram_free != null
                      ? t("serverRamValue", {
                        free: (item.server_ram_total < 0 ? 0 : item.server_ram_total) - (item.server_ram_free < 0 ? 0 : item.server_ram_free),
                        total: item.server_ram_total < 0 ? 0 : item.server_ram_total,
                      })
                      : t("unknown")}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <Box component="span" sx={{ color: "gray" }}>{t("cpuAvarageUsage")}: </Box>
                    <div>
                      {!item.cpu_load_avg ? "0" : Object.entries(item.cpu_load_avg).map(([key, value]) => (
                        <p key={key} style={{ margin: 0 }}>
                          {t("duringKey", { key, value: value === -1 ? "-" : value * 100 + "%" })}
                        </p>
                      ))}
                    </div>
                  </Typography>
                </Grid>
              </Grid>

              <div>
                <ASelectPopover
                  loading={
                    item.type === "TES"
                      ? switchTesModeMutation.isPending
                      : switchGsModeMutation.isPending
                  }
                  containerStyle={{ display: "flex" }}
                  buttonText={t("ok")}
                  disabled={newExploitationStageValue === undefined}
                  onOk={() =>
                    handleSwitchMode(
                      item,
                      newExploitationStageValue as unknown as SwitchTesModeModeEnum,
                    )
                  }
                  popoverProps={{
                    title: t("chooseMode"),
                    open: item.id === changingExploitationStageService,
                    trigger: "click",
                    onOpenChange: (popoverProps) => {
                      if (!popoverProps) {
                        resetSwitchExploitationStageState();
                      }
                    },
                  }}
                  selectProps={{
                    style: { width: 150 },
                    value: newExploitationStageValue,
                    onChange: (value) => setNewExploitationStageValue(value),
                    allowClear: true,
                    options: getServiceExploitationStageSelectOptions(item.mode),
                    placeholder: t("chooseMode"),
                  }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => setChangingExploitationStageService(item.id)}
                  >
                    {t("switchMode")}
                  </Button>
                </ASelectPopover>
              </div>
            </Box>
          </Paper>
        ))}
      </Stack>*/}
    </>
  );
};

export default RunningServicesModule;
